/**
 * NOTE:`data` in this context can represent many different objects. E.g. Volunteer data, Career Share data, Request Data.
 * This is generalised so many types of use cases can utilise these functions.
 */
import i18next from 'i18next';
import { tagOptions, stateOptions } from '@/data/formData';

/**
 * Given a volunteer object, will return their full name formatted as a string
 * @param {object} data
 * @returns {string} Full name of Volunteer
 */
const getVolunteerFullName = (data = {}) => {
    if (!data) return '';
    const { preferredName, firstName, lastName, surname } = data;

    const displayFirstName = preferredName || firstName || '';
    const displayLastName = lastName || surname || '';

    if (!displayFirstName && !displayLastName) return '';
    if (!displayFirstName) return i18next.t('%(lastName)s (surname)', { lastName: displayLastName, interpolation: { escapeValue: false } }).trim();
    return `${displayFirstName} ${displayLastName}`.trim();
};

/**
 * Given a volunteer object, will return what their job title formatted as a string.
 * @param {object} data
 * @returns {string} Job title
 */
const getVolunteerTitle = (data = {}) => {
    const { title = '' } = data ?? {};

    if (!title || typeof title !== 'string') return i18next.t('Unknown Job Title');
    return title;
};

/**
 * Given a volunteer object, returns their avatar URL or null if unavailable.
 * @param {object} data
 * @returns {string|null} Avatar URL
 */
const getVolunteerProfileImg = (data = {}) => {
    const { avatar_url: avatarURL = null } = data ?? {};

    // Image URL's passed into <img> tags can handle null URL's.
    // No fallback needed.
    if (typeof avatarURL !== 'string') return null;
    return avatarURL;
};

const generateVolunteerDetailsLink = (volunteerId) => `/volunteer/${volunteerId}`;

/**
 * Returns a truncated volunteer description capped at 25 words.
 * Org admins receive an empty string when no description exists; other roles receive a fallback prompt.
 * @param {string} description
 * @param {boolean} orgAdmin
 * @returns {string}
 */
const displayDescriptionSummary = (description, orgAdmin) => {
    if (!description) return orgAdmin ? '' : i18next.t('Reach out to this volunteer to learn more about their career path and profession.');
    const words = description.split(' ');

    if (words.length < 25) return description;
    return `${words.slice(0, 25).join(' ')}...`;
};

/**
 * Returns a formatted location string from volunteer or site address fields.
 * Falls back to site city/state when volunteer city/state are absent.
 * @param {string} city
 * @param {string} state
 * @param {string} siteCity
 * @param {string} siteState
 * @returns {string}
 */
const displayLocation = (city, state, siteCity, siteState) => {
    const resolvedCity = city || siteCity;
    const resolvedState = state || stateOptions.find((s) => s.fullname === siteState)?.value;
    return [resolvedCity, resolvedState].filter(Boolean).join(', ');
};

/**
 * Returns a display-ready array of tag label strings, capped at 2 with a "+N" overflow entry.
 * Industry is prepended if not already present in tags.
 * @param {string[]} tags
 * @param {string|object} industry
 * @returns {string[]}
 */
const displayVolunteerTagSummary = (tags, industry) => {
    const toTagOptionData = (tag) => tagOptions().find((each) => each.value === tag);

    const processableTags = (tags ?? []).map((tag) => toTagOptionData(tag)?.text || i18next.t('Unknown'));

    if (industry && !tags?.includes(industry)) {
        // Move industry tag to the front of the list, guarantees it displays first.
        processableTags.unshift(typeof industry === 'object' ? industry.label : industry);
    }

    if (processableTags.length <= 2) return processableTags;

    const firstTwoTags = processableTags.slice(0, 2);
    const displayExtraTagsCount = `+${processableTags.length - 2}`;
    return [...firstTwoTags, displayExtraTagsCount];
};

const profileConsideredIncomplete = (rawEmployeeData) => {
    const incompleteFields = [];
    const { tags, avatarUrl, description } = rawEmployeeData;

    if (!avatarUrl || avatarUrl === '') incompleteFields.push('avatarUrl');
    if (!description || description === '') incompleteFields.push('description');
    if (!tags || tags.length === 0) incompleteFields.push('tags');

    return incompleteFields;
};

const profileConsideredMalformed = (rawEmployeeData) => {
    const incompleteFields = [];
    const { tags } = rawEmployeeData;

    if (tags && tags.length > 3) incompleteFields.push('has listed too many areas of expertise');

    return incompleteFields;
};

/**
 * Given a list of employees and the current employee's id, returns the list without the current employee.
 * @param {object[]} employees
 * @param {string|number|null} [currentEmployeeId]
 * @returns {object[]} Employees other than the current one
 */
const excludeEmployee = (employees, currentEmployeeId) => {
    if (!Array.isArray(employees)) return [];
    if (!currentEmployeeId) return employees;
    return employees.filter((employee) => employee.employeeId !== currentEmployeeId);
};

/**
 * Converts a `VolunteerSummary` record from `GET /v2/volunteers` (snake_case) into the camelCase shape
 * the volunteer card and display utils expect. Fields that are already single words pass through untouched.
 * @param {object} record - A VolunteerSummary from the API
 * @returns {object} Volunteer with camelCase keys
 */
const parseVolunteer = (record) => {
    const {
        employee_id: employeeId,
        first_name: firstName,
        last_name: lastName,
        preferred_name: preferredName,
        avatar_url: avatarUrl,
        career_share_count: opportunityCount,
        organizations,
        ...rest
    } = record;

    return {
        ...rest,
        employeeId,
        firstName,
        lastName,
        preferredName,
        avatarUrl,
        opportunityCount,
        organizations: (organizations ?? []).map(
            ({
                organization_id: organizationId,
                avatar_url: orgAvatarUrl,
                organization_type: organizationType,
                org_deleted: orgDeleted,
                ...org
            }) => ({
                ...org,
                organizationId,
                avatarUrl: orgAvatarUrl,
                organizationType,
                orgDeleted,
            }),
        ),
    };
};

export {
    getVolunteerFullName,
    getVolunteerTitle,
    getVolunteerProfileImg,
    displayDescriptionSummary,
    displayLocation,
    displayVolunteerTagSummary,
    generateVolunteerDetailsLink,
    profileConsideredIncomplete,
    profileConsideredMalformed,
    excludeEmployee,
    parseVolunteer,
};
